import { getSessionUuid, getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import { Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import React, { useEffect, useState } from 'react';

const IntegrationsCallbackPage: React.FC = () => {
  const query = useLocationQuery();
  const organizationId = useAppSelector((state: RootState) => state.organization?.organizationId);
  const { rememberMe } = useAppSelector((state: RootState) => state.user);
  const [success, setSuccess] = useState(false);
  const [successSlack, setSuccessSlack] = useState(false);
  const [backToIntegrations, setBackToIntegrations] = useState(false);
  const [failure, setFailure] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const localeIntegrations = useTranslation('integrations');

  useEffect(() => {
    // Handle Slack OAuth callback
    const slackConnected = query.get('slack_connected');
    const slackError = query.get('slack_error');

    if (slackConnected === 'true') {
      setSuccess(true);
      setSuccessSlack(true);
      setBackToIntegrations(true);
      return;
    }

    if (slackError) {
      setFailure(true);
      setErrorMessage(slackError);
      setBackToIntegrations(true);
      return;
    }

    // Handle Google OAuth callback
    const code = query.get('code');
    const scope = query.get('scope');
    const state = query.get('state');
    if (code && scope && state) {
      redeemCode(code, scope, state);
    }
  }, [query]);

  // This function calls the backend to activate the integration
  const redeemCode = async (code: string, scope: string, state: string) => {
    const url = process.env.REACT_APP_BACKEND_URL + 'auth/' + 'redeemIntegrationCode';
    const payload = {
      code,
      scope,
      state,
    };
    if (url) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${rememberMe ? getTokenLocalStorage() : getTokenSessionStorage()}`,
            'X-Organization-Id': organizationId || '',
            'Session-Uuid': getSessionUuid(),
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (data.messages[0] === 'OK') {
          setSuccess(true);
        }

        if (data?.statusCode === 500) {
          setFailure(true);
        }
      } catch {
        setFailure(true);
      }
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: 'linear-gradient(45deg, #ECEFF1, #FFFFFF)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Container maxWidth="sm" className="text-center">
        {!success && !failure && (
          <Typography variant="h4">
            {localeIntegrations['enablingIntegration']}
            <CircularProgress size={24} className="ml-2" />
          </Typography>
        )}

        {success && <Typography variant="h4">{localeIntegrations['success']}</Typography>}
        {successSlack && <Typography variant="h4">{localeIntegrations['successSlack']}</Typography>}
        {backToIntegrations && <Button>{localeIntegrations['backToIntegrations']}</Button>}


        {failure && (
          <Typography variant="h4">
            {errorMessage
              ? localeIntegrations['slackConnectionFailed'].replace('{error}', errorMessage)
              : localeIntegrations['error']}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default IntegrationsCallbackPage;
